import type { NavigableWorker } from '@/frontend/types/inertia'

// Client-side mirror of the member GET routes under
// /dashboard/digital_workers/:id — the Show sections
// (Dashboard::DigitalWorkersController::WORKER_SECTIONS plus
// `configuracoes`, which has its own action) and, since C.2 (SZ-1597), the
// canonical agent-scoped org pages (processos, publicacoes, integracoes,
// credenciais, auditoria). Switching agents on any of them stays in the same
// section of the new agent (A.2, SZ-1580).
export const WORKER_SECTIONS = [
  'comunicacao',
  'webhooks',
  'uso',
  'rotinas',
  'memoria',
  'evals',
  'auditoria',
  'configuracoes',
  'processos',
  'publicacoes',
  'integracoes',
  'credenciais'
] as const

export type WorkerSection = (typeof WORKER_SECTIONS)[number]

// Mirror of Dashboard::DigitalWorkersController::READY_GATED_SECTIONS: these
// sections need a ready OpenClaw instance (the server redirects non-ready
// workers back to the worker landing; WorkerNav disables the links).
const READY_GATED_SECTIONS: readonly WorkerSection[] = ['rotinas', 'memoria', 'webhooks', 'evals']

export function isReadyGatedSection(section: WorkerSection): boolean {
  return READY_GATED_SECTIONS.includes(section)
}

function isWorkerSection(segment: string): segment is WorkerSection {
  return (WORKER_SECTIONS as readonly string[]).includes(segment)
}

/**
 * The section segment when `path` is a worker section page or one of its
 * descendants (/dashboard/digital_workers/:id/<section>/...), null anywhere
 * else (worker landing, org-wide pages, chat…). Descendants preserve their
 * owning section when switching agents; Processos detail is the current
 * canonical example.
 */
export function workerSectionFromPath(path: string): WorkerSection | null {
  const segment = path.match(/^\/dashboard\/digital_workers\/[^/]+\/([^/?#]+)(?:\/|$)/)?.[1]
  if (!segment) return null
  return isWorkerSection(segment) ? segment : null
}

/**
 * Whether `worker` can land on `section` directly — the same gating WorkerNav
 * applies to its links (provisioning only; `probe_connect_ready` gates Chat,
 * not sections).
 */
export function isSectionAvailable(section: WorkerSection, worker: NavigableWorker): boolean {
  return !isReadyGatedSection(section) || worker.provisioning_status === 'ready'
}
